import { alpha, Box, Divider, Typography, useTheme } from "@mui/material";
import Grid from '@mui/material/Grid';
import { ProductiveUnit } from "../../models/productiveUnitModels";
import { useEffect, useState } from "react";
import { AppTheme } from "../../theme";
import { createProductiveUnitInfoPageStyle } from "./ProductiveUnitInfoPageStyles";
import useLoading from "../../lib/hooks/useLoading";
import { getProductiveUnitByUid } from "../../services/publicServices";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/utils";
import { LoadingProgress } from "../../lib/components/LoadingProgress/LoadingProgress";
import Map from "../../general/components/Map";

const ProductiveUnitInfoRow = ({ title, value }:{ title:string, value?:string | number }) => {
    const theme: AppTheme = useTheme();
    return (
        <Grid container size={12} spacing={2} sx={{height: "fit-content"}}>
            <Grid size={"auto"}>
                <Typography variant="body2" sx={{ color: theme.palette.neutral_2 }}>{title}</Typography>
            </Grid>
            <Grid size={"grow"} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <Typography variant="caption2" align="right" sx={{ fontWeight: 700 }}>{value ? value : "--"}</Typography>
            </Grid>
        </Grid>
    )
}


export default function ProductiveUnitInfoPage() {
    const theme: AppTheme = useTheme();
    const [productiveUnit, setProductiveUnit] = useState<ProductiveUnit|null>(null);
    const [loading, setLoading] = useLoading(false);
    const params = useParams();
    const [t] = useTranslation();

    const styles = createProductiveUnitInfoPageStyle(theme);

    useEffect(() => {
        loadProdUnitByUid();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProdUnitByUid = async () => {
        setLoading(true);
        try {
            const pUnitRes = await getProductiveUnitByUid(
                params.productiveUnitUid as string
            );
            setProductiveUnit(pUnitRes);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStageDesc = (code: string) => {
        switch (code) {
            case "PRE":
                return t("PRODUCTIVE_UNIT.STAGE_1")
            case "SIE":
                return t("PRODUCTIVE_UNIT.STAGE_2")
            case "DES":
                return t("PRODUCTIVE_UNIT.STAGE_3")
            case "FLO":
                return t("PRODUCTIVE_UNIT.STAGE_4")
            case "COS":
                return t("PRODUCTIVE_UNIT.STAGE_5")
            case "FCO":
                return t("PRODUCTIVE_UNIT.STAGE_6")
            case "CER":
                return t("PRODUCTIVE_UNIT.STAGE_FINAL")
            default:
                return t("PRODUCTIVE_UNIT.STAGE_UNKNOWN")
        }
    };


    return (
        <>
            <Box sx={{ display: 'flex' }}>
                <Box
                    component={'main'}
                    sx={[styles.main]}
                >
                    <>
                        {
                            productiveUnit != null &&
                                <Grid container spacing={{xs:2,md:4}} sx={[styles.card]}>
                                    <Grid sx={{ position: "absolute", top:0, left:0, backgroundColor: alpha(theme.palette.primary.main, .75), width: "100%", maxWidth:{xs:"150px", md:"350px"}, height:"14px", clipPath: "polygon(0 0, 100% 0, 95% 100%, 0% 100%)" }}/>
                                    <Grid sx={{ position: "absolute", top:0, left:0, backgroundColor: alpha(theme.palette.primary.main, .75), width: "100%", height:{xs:"150px", md:"350px"}, maxWidth:"14px", clipPath: "polygon(0 0, 100% 0, 100% 95%, 0% 100%)" }}/>

                                    <Grid container spacing={2} size={{xs:12, md:6}} sx={{ height: "fit-content" }}>
                                        <Grid size={{xs:12}} sx={[styles.logoWrapperMobile]}>
                                            <Box component={"p"} sx={[styles.logo]} />
                                        </Grid>
                                        <Grid container size={12} sx={[styles.mapWrapper]}>
                                            <Grid size={12} sx={{ borderRadius:{xs:"8px",md:0}, overflow: "hidden"}}>
                                                <Map location={productiveUnit?.location || ""} shape="" showLocation/>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1} size={12} sx={[styles.detailCard]}>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.PRODUCTIVE_UNIT")} value={productiveUnit?.description} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.DIVISION")} value={productiveUnit?.division?.description} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.FARM_INASE")} value={productiveUnit?.farm_inase} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.SEED_LOT")} value={productiveUnit?.seed_lot} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.SEED_LOT")} value={productiveUnit?.seed_lot} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.FARM")} value={productiveUnit?.farm.description} />
                                            <Grid container size={12}>
                                                <Divider sx={{ width:"100%", borderBottomWidth: 1, backgroundColor: theme.palette.neutral_2, my: "8px" }}/>
                                            </Grid>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.PRODUCER")} value={`${productiveUnit.producer.code} - ${productiveUnit.producer.user.last_name}${productiveUnit.producer.user.first_name && productiveUnit.producer.user.first_name.trim().length > 1 && `, ${productiveUnit.producer.user.first_name}` }`} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.TECHNICIAN")} value={productiveUnit?.technician ? `${productiveUnit.technician.code} - ${productiveUnit.technician.user.last_name}${productiveUnit.technician.user.first_name && productiveUnit.technician.user.first_name.trim().length > 1 && `, ${productiveUnit.technician.user.first_name}` }` :  t("COMMON.UNASSIGNED")} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.ASSIGMENT_DATE")} value={productiveUnit?.technician_assigment_date != null ? formatDate(productiveUnit?.technician_assigment_date) : "--"} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.ASSIGMENT_DATE")} value={productiveUnit?.technician_assigment_date != null ? formatDate(productiveUnit?.technician_assigment_date) : "--"} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.STAGE")} value={getStageDesc(productiveUnit?.actual_pru_stage.code || "--")} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.STAGE_DATE")} value={productiveUnit?.actual_stage_date != null ? formatDate(productiveUnit?.actual_stage_date) : "--"} />
                                        </Grid>
                                        <Grid container spacing={1} size={12} sx={[styles.detailCard]}>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.HARVEST")} value={productiveUnit?.harvest == null ? t("COMMON.UNASSIGNED") :productiveUnit?.harvest.description} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.INPUT")} value={productiveUnit?.input == null ? t("COMMON.UNASSIGNED") :productiveUnit?.input.description} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.CROP")} value={productiveUnit?.crop == null ? t("COMMON.UNASSIGNED") :productiveUnit?.crop.description} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.PRODUCT")} value={productiveUnit?.product == null ? t("COMMON.UNASSIGNED") :productiveUnit?.product.description} />
                                        </Grid>
                                    </Grid>
                                    
                                    <Grid container spacing={2} size={{xs:12, md:6}} sx={{height: "fit-content"}}>
                                        <Grid size={{xs:12}} sx={[styles.logoWrapper]}>
                                            <Box component={"p"} sx={[styles.logo]} />
                                        </Grid>
                                        <Grid container spacing={1} size={12} sx={[styles.detailCard]}>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.SOWING_DATE")} value={productiveUnit?.sowing_date != null ? formatDate(productiveUnit?.sowing_date) : "--"} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.ESTIMATED_HARVEST_DATE")} value={productiveUnit?.estimated_harvest_date?.description || "--"} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.CLOSING_DATE")} value={productiveUnit?.closing_date != null ? formatDate(productiveUnit?.closing_date) : "--"} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.DEVELOP_DATE")} value={productiveUnit?.development_date != null ? formatDate(productiveUnit?.development_date) : "--"}/>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.HARVEST_DATE")} value={productiveUnit?.harvest_date != null ? formatDate(productiveUnit?.harvest_date) : "--"} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.HARVEST_METHOD")} value={productiveUnit?.harvest_method != null ? productiveUnit?.harvest_method : "--"} />
                                        </Grid>
                                        <Grid container spacing={1} size={12} sx={[styles.detailCard]}>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.HECTARES_PU")} value={productiveUnit?.hectares} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.ESTIMATED_HARVEST_HECTARES")} value={productiveUnit?.estimated_harvest_hectares} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.SOWING_HECTARES")} value={productiveUnit?.sowing_hectares} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.HARVEST_HECTARES")} value={productiveUnit?.harvest_hectares} />
                                        </Grid>
                                        <Grid container spacing={1} size={12} sx={[styles.detailCard]}>
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.PLANTSXM")} value={productiveUnit?.plantsxm2} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.TOTAL_YIELD")} value={productiveUnit?.total_yield} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.TOTAL_OWN_USE_DELIVERED")} value={productiveUnit?.total_own_use_delivered} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.ESTIMATED_YIELD")} value={productiveUnit?.estimated_yield} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.TOTAL_OWN_USE_FARM")} value={productiveUnit?.total_own_use_farm} />
                                            <ProductiveUnitInfoRow title={t("PRODUCTIVE_UNIT.TOTAL_REAL")} value={productiveUnit?.total_real} />
                                        </Grid>
                                    </Grid>
                                </Grid>
                        }
                    </>
                </Box>
            </Box>
            {loading && <LoadingProgress />}
        </>
    );
}